import React, { useEffect, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InventoryItem, InventoryLot, Product, inventoryItemsAPI, inventoryLotsAPI } from '../../services/api';
import { Button, Modal, Spinner } from '../ui';
import InventoryLocationPicker from './InventoryLocationPicker';

type StockRow = {
  key: string;
  itemId?: number;
  trayId: string;
  lotId: string;
  quantity: string;
  unit: string;
  status: InventoryItem['status'];
  batchNumber: string;
  expiryDate: string;
  notes: string;
  initialItem?: InventoryItem;
};

interface ProductStockEditorProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onSuccess: () => void;
}

const newStockRow = (unit = ''): StockRow => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  trayId: '',
  lotId: '',
  quantity: '',
  unit,
  status: 'IN_STOCK',
  batchNumber: '',
  expiryDate: '',
  notes: '',
});

const formatDateInput = (value?: string) => {
  if (!value) return '';
  return value.split('T')[0];
};

const rowFromItem = (item: InventoryItem, fallbackUnit = ''): StockRow => ({
  key: `item-${item.id}`,
  itemId: item.id,
  trayId: item.trayId?.toString() || item.tray?.id?.toString() || '',
  lotId: item.lotId?.toString() || '',
  quantity: item.quantity.toString(),
  unit: item.unit || item.product?.unit || fallbackUnit,
  status: item.status,
  batchNumber: item.lotId ? '' : item.batchNumber || '',
  expiryDate: formatDateInput(item.expiryDate),
  notes: item.notes || '',
  initialItem: item,
});

const ProductStockEditor: React.FC<ProductStockEditorProps> = ({ isOpen, product, onClose, onSuccess }) => {
  const [rows, setRows] = useState<StockRow[]>([]);
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isOpen && product) {
      loadData();
    }
  }, [isOpen, product?.id]);

  const loadData = async () => {
    if (!product) return;

    try {
      setLoading(true);
      setMessage(null);
      const [items, lotData] = await Promise.all([
        inventoryItemsAPI.getAll({ productId: product.id }),
        inventoryLotsAPI.getAll({ isActive: true }),
      ]);
      setRows(items.length > 0 ? items.map((item) => rowFromItem(item, product.unit || '')) : [newStockRow(product.unit || '')]);
      setLots(lotData);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load product stock' });
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (key: string, patch: Partial<StockRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
  };

  const handleSave = async () => {
    if (!product) return;

    const invalidRow = rows.find((row) => !row.trayId || Number(row.quantity) <= 0);
    if (invalidRow) {
      setMessage({ type: 'error', text: 'Every stock row needs a location and quantity greater than zero.' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      for (const row of rows) {
        const payload: any = {
          productId: product.id,
          trayId: Number(row.trayId),
          quantity: Number(row.quantity),
          unit: row.unit.trim() || product.unit || undefined,
          status: row.status,
        };

        if (row.lotId) {
          payload.lotId = Number(row.lotId);
        } else if (row.batchNumber.trim()) {
          payload.batchNumber = row.batchNumber.trim();
        }
        if (row.expiryDate) payload.expiryDate = new Date(row.expiryDate).toISOString();
        if (row.notes.trim()) payload.notes = row.notes.trim();

        if (row.itemId) {
          await inventoryItemsAPI.update(row.itemId, payload);
        } else {
          await inventoryItemsAPI.create(payload);
        }
      }

      setMessage({ type: 'success', text: 'Product stock updated' });
      await loadData();
      onSuccess();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save product stock' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: StockRow) => {
    if (!row.itemId) {
      setRows((current) => current.length === 1 ? [newStockRow()] : current.filter((item) => item.key !== row.key));
      return;
    }

    if (confirmDeleteId !== row.itemId) {
      setConfirmDeleteId(row.itemId);
      return;
    }

    try {
      setDeletingId(row.itemId);
      setMessage(null);
      await inventoryItemsAPI.delete(row.itemId);
      setConfirmDeleteId(null);
      await loadData();
      onSuccess();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete stock row' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? `Edit Stock: ${product.name}` : 'Edit Stock'}
      subtitle={product ? `${product.sku} - ${product.unit || 'units'}` : undefined}
      size="full"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {message && (
            <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}>
              {message.text}
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Inventory Rows</h3>
              <p className="mt-1 text-xs text-neutral-500">Edit the exact quantities by lot, status, and warehouse location.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRows((current) => [...current, newStockRow(product?.unit || '')])}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Add Stock Row
            </Button>
          </div>

          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.key} className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
                  <div className="lg:col-span-12">
                    <InventoryLocationPicker
                      trayId={row.trayId}
                      onTrayIdChange={(trayId) => updateRow(row.key, { trayId })}
                      initialTray={row.initialItem?.tray || null}
                      required
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <label className="block text-xs font-medium text-neutral-600">Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={row.quantity}
                      onChange={(event) => updateRow(row.key, { quantity: event.target.value })}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div className="lg:col-span-1">
                    <label className="block text-xs font-medium text-neutral-600">Unit</label>
                    <input
                      type="text"
                      value={row.unit}
                      onChange={(event) => updateRow(row.key, { unit: event.target.value })}
                      placeholder={product?.unit || 'unit'}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-medium text-neutral-600">Status</label>
                    <select
                      value={row.status}
                      onChange={(event) => updateRow(row.key, { status: event.target.value as InventoryItem['status'] })}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="IN_STOCK">In Stock</option>
                      <option value="LOW_STOCK">Low Stock</option>
                      <option value="RESERVED">Reserved</option>
                      <option value="DAMAGED">Damaged</option>
                      <option value="OUT_OF_STOCK">Out of Stock</option>
                    </select>
                  </div>
                  <div className="lg:col-span-3">
                    <label className="block text-xs font-medium text-neutral-600">Lot</label>
                    <select
                      value={row.lotId}
                      onChange={(event) => updateRow(row.key, { lotId: event.target.value, batchNumber: event.target.value ? '' : row.batchNumber })}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">Create/find by lot number</option>
                      {lots.map((lot) => (
                        <option key={lot.id} value={lot.id}>
                          {lot.lotNumber}{lot.supplier?.name ? ` - ${lot.supplier.name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-medium text-neutral-600">New Lot Number</label>
                    <input
                      type="text"
                      value={row.batchNumber}
                      onChange={(event) => updateRow(row.key, { batchNumber: event.target.value })}
                      disabled={Boolean(row.lotId)}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-60"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-medium text-neutral-600">Expiry</label>
                    <input
                      type="date"
                      value={row.expiryDate}
                      onChange={(event) => updateRow(row.key, { expiryDate: event.target.value })}
                      className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-end justify-end lg:col-span-1">
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      disabled={deletingId === row.itemId}
                      className="rounded-md p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-error-700 disabled:opacity-50"
                      title={confirmDeleteId === row.itemId ? 'Confirm delete stock row' : 'Delete stock row'}
                    >
                      {confirmDeleteId === row.itemId ? <CheckIcon className="h-4 w-4" /> : <TrashIcon className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="lg:col-span-12">
                    <input
                      type="text"
                      value={row.notes}
                      onChange={(event) => updateRow(row.key, { notes: event.target.value })}
                      placeholder="Optional row notes"
                      className="block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-2 border-t border-neutral-200 bg-white px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button type="button" variant="primary" onClick={handleSave} isLoading={saving}>
              Save Stock
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProductStockEditor;
